import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import type { ChatMention } from "../types/domain";
import { normalizeForSearch as normalize } from "../util/StringUtils";

const MAX_SUGGESTIONS = 8;

interface UseMentionAutocompleteArgs {
  value: string;
  setValue: (v: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  mentionable: ChatMention[];
  pendingMentions: ChatMention[];
  setPendingMentions: (updater: (prev: ChatMention[]) => ChatMention[]) => void;
}

interface UseMentionAutocompleteReturn {
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  overlay: ReactNode;
}

interface QueryState {
  atIndex: number;
  query: string;
}

/**
 * Given text and a caret position, detect an active `@query` (from the last `@`
 * before the caret, up to the caret, provided no whitespace comes between).
 */
const detectQuery = (text: string, caret: number): QueryState | null => {
  const upTo = text.slice(0, caret);
  const atIdx = upTo.lastIndexOf("@");
  if (atIdx < 0) return null;
  // The @ must be at the start of input or preceded by whitespace, to avoid
  // matching things like emails.
  if (atIdx > 0 && !/\s/.test(text[atIdx - 1])) return null;
  const query = upTo.slice(atIdx + 1);
  // Whitespace ends the query.
  if (/\s/.test(query)) return null;
  return { atIndex: atIdx, query };
};

const searchTermsFor = (m: ChatMention): string[] => {
  const terms = new Set<string>();
  terms.add(m.name);
  (m.searchTerms ?? []).forEach((t) => t && terms.add(t));
  return Array.from(terms).map(normalize);
};

const filterMentions = (mentionable: ChatMention[], query: string): ChatMention[] => {
  const q = normalize(query.trim());
  if (q.length === 0) return mentionable.slice(0, MAX_SUGGESTIONS);

  const matched = mentionable
    .map((m) => ({ mention: m, terms: searchTermsFor(m) }))
    .filter(({ terms }) => terms.some((t) => t.includes(q)));

  // Ranking: prefer prefix match on any term, then name-prefix, then alphabetical.
  matched.sort((a, b) => {
    const aPrefix = a.terms.some((t) => t.startsWith(q)) ? 0 : 1;
    const bPrefix = b.terms.some((t) => t.startsWith(q)) ? 0 : 1;
    if (aPrefix !== bPrefix) return aPrefix - bPrefix;
    return a.mention.name.localeCompare(b.mention.name);
  });

  return matched.map(({ mention }) => mention).slice(0, MAX_SUGGESTIONS);
};

export const useMentionAutocomplete = ({
  value,
  setValue,
  inputRef,
  mentionable,
  pendingMentions: _pendingMentions,
  setPendingMentions,
}: UseMentionAutocompleteArgs): UseMentionAutocompleteReturn => {
  const [queryState, setQueryState] = useState<QueryState | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const suggestions = useMemo(
    () => (queryState ? filterMentions(mentionable, queryState.query) : []),
    [queryState, mentionable],
  );

  useEffect(() => {
    // Keep active index in range whenever suggestions change.
    if (activeIdx >= suggestions.length) setActiveIdx(0);
  }, [suggestions, activeIdx]);

  const close = useCallback(() => {
    setQueryState(null);
    setActiveIdx(0);
  }, []);

  const applyMention = useCallback((mention: ChatMention) => {
    if (!queryState) return;
    const before = value.slice(0, queryState.atIndex);
    const after = value.slice(queryState.atIndex + 1 + queryState.query.length);
    const inserted = `@${mention.name} `;
    const nextValue = before + inserted + after;
    setValue(nextValue);
    setPendingMentions(prev => (prev.some(m => m.playerId === mention.playerId) ? prev : [...prev, mention]));
    close();
    // Restore caret after inserted text.
    const caret = (before + inserted).length;
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  }, [queryState, value, setValue, setPendingMentions, close, inputRef]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    setValue(nextValue);
    const caret = e.target.selectionStart ?? nextValue.length;
    const q = detectQuery(nextValue, caret);
    setQueryState(q);
  }, [setValue]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (!queryState || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      applyMention(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  }, [queryState, suggestions, activeIdx, applyMention, close]);

  useEffect(() => {
    if (!queryState) return;
    const onDocMouseDown = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (popoverRef.current && popoverRef.current.contains(target)) return;
      if (inputRef.current && inputRef.current.contains(target)) return;
      close();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [queryState, close, inputRef]);

  const overlay = queryState && suggestions.length > 0 ? (
    <div ref={popoverRef} className="chat-mention-popover" role="listbox">
      {suggestions.map((s, i) => (
        <div
          key={s.playerId}
          className={`chat-mention-item${i === activeIdx ? " active" : ""}`}
          role="option"
          aria-selected={i === activeIdx}
          onMouseDown={(ev) => {
            // Prevent input blur before selection commits.
            ev.preventDefault();
            applyMention(s);
          }}
          onMouseEnter={() => setActiveIdx(i)}
        >
          <span className="chat-mention-item-at">@</span>
          {s.name}
        </div>
      ))}
    </div>
  ) : null;

  return { handleChange, handleKeyDown, overlay };
};
